import {
  FlexColumn,
  RadioListInput,
} from '@equifinance/shared';
import styled from 'styled-components';
import {
  useAccountContext,
} from '../../../../../../contexts';
import Section from '../../../../components/Section';
import {
  DirectorOfLimitedCompanyEntry,
  EmploymentHistory, EmploymentWithIncome, IncomeEvidence,
  LimitedLiabilityPartnershipEntry,
  PartnershipEntry,
  SoleTraderEntry,
} from '../../../../../../contexts/account/IncomeAndEmployment';
import { ineSubSectionHeaderCss } from '../IncomeAndEmploymentSection';
import { SA302Evidence } from './SA302Evidence';
import { PayslipEvidence } from './PayslipEvidence';
import { AccountantCertificateEvidence } from './AccountantCertificateEvidence';
import { AuditedAnnualAccountEvidence } from './AuditedAnnualAccountEvidence';

const EvidenceOptionsContainer = styled(FlexColumn)`${({ theme }) => `
  background-color: ${theme.colours.neutral[10]};
  padding: ${theme.padding[7]};
`}`;

const EvidenceContentContainer = styled(FlexColumn)`${({ theme }) => `
  padding: ${theme.padding[7]};
  background: ${theme.colours.neutral[9]};
  box-shadow: inset 0px 8px 8px -4px rgba(0,0,0,0.05);
`}`;

const employmentTypesWithIncome: EmploymentHistory['employmentType'][] = [
  'Employed',
  'ConstructionIndustryScheme',
  'DirectorOfLimitedCompany',
  'FosterCarer',
  'LimitedLiabilityPartnership',
  'Partnership',
  'SoleTrader',
];

const getEvidenceDocumentOptions = (possibleEvidenceTypes: string[]) => (
  Object.entries(IncomeEvidence)?.reduce((prev, [key, value]) => {
    if (possibleEvidenceTypes.includes(key)) {
      return {
        ...prev,
        [key]: value,
      };
    }
    return prev;
  }, {} as Record<string, string>)
);

export default function IncomeSection({
  employmentHistory,
  applicantIndex,
  employmentHistoryIndex,
}: {
  employmentHistory: EmploymentHistory,
  applicantIndex: number,
  employmentHistoryIndex: number,
}) {
  const { account } = useAccountContext();
  const { errors, warnings } = account;
  const { id, possibleEvidenceTypes, employmentType } = employmentHistory as EmploymentWithIncome;
  const fieldPath = `applicants[${applicantIndex}].incomeAndEmployment.employmentHistory[${employmentHistoryIndex}]`;
  const { evidenceType } = employmentHistory as EmploymentWithIncome;

  const renderEvidences = () => {
    switch (evidenceType) {
      case 'SA302':
        return <SA302Evidence field={fieldPath} />;
      case 'RemittanceSlips':
        return <PayslipEvidence field={fieldPath} payslipsEvidence="remittanceSlipsEvidence" />;
      case 'Payslips':
        return <PayslipEvidence field={fieldPath} payslipsEvidence="payslipsEvidence" />;
      case 'AccountantCertificate':
        return (
          <AccountantCertificateEvidence
            employmentType={employmentType}
            field={fieldPath}
            evidences={(employmentHistory as unknown as (SoleTraderEntry
              | PartnershipEntry
              | DirectorOfLimitedCompanyEntry
              | LimitedLiabilityPartnershipEntry)).accountantCertificateEvidence ?? {}}
          />
        );
      case 'AuditedAccounts':
        return (
          <AuditedAnnualAccountEvidence
            field={fieldPath}
            evidences={(employmentHistory as SoleTraderEntry).auditedAccountsEvidence ?? {}}
          />
        );
      default: return null;
    }
  };

  if (
    !possibleEvidenceTypes?.length
    || !employmentTypesWithIncome.includes(employmentType)
  ) {
    return null;
  }

  return (
    <Section
      id={id}
      title="Income"
      level={2}
      errors={errors}
      warnings={warnings}
      headerCss={ineSubSectionHeaderCss}
      titleIcon={<></>}
    >
      <EvidenceOptionsContainer>
        <RadioListInput
          label="Document used to evidence income"
          field={`${fieldPath}.evidenceType`}
          disabled={possibleEvidenceTypes.length <= 1}
          options={getEvidenceDocumentOptions(possibleEvidenceTypes || [])}
          required
        />
      </EvidenceOptionsContainer>
      {evidenceType && (
        <EvidenceContentContainer>
          {renderEvidences()}
        </EvidenceContentContainer>
      )}
    </Section>
  );
}
